<template>
  <div class="prepeers-search-bar">
    <div class="prepeers-search-bar__field-wrap">
      <svg
        class="prepeers-search-bar__icon"
        width="18"
        height="18"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true">
        <circle
          cx="6.5"
          cy="6.5"
          r="4.5"
          stroke="currentColor"
          stroke-width="1.3" />
        <path
          d="M10.5 10.5L14 14"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linecap="round" />
      </svg>
      <input
        :id="inputId"
        v-model="localQuery"
        class="prepeers-search-bar__input"
        type="search"
        :placeholder="placeholder"
        autocomplete="off"
        :autofocus="props.autofocus"
        @keydown.enter.prevent="emitSearch" />
    </div>
    <PrePeersButton
      v-if="props.showSubmitButton"
      variant="primary"
      size="sm"
      native-type="button"
      class="prepeers-search-bar__btn"
      @click="emitSearch">
      {{ submitLabel }}
    </PrePeersButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import PrePeersButton from '~/components/core/PrePeersButton.vue'
import type { PrePeersSearchBarProps } from '~/types/PrePeersSearchBar'

const FALLBACK_INPUT_ID: string = `prepeers-search-bar-${Math.random().toString(36).slice(2, 9)}`

/**
 * Définit les props du composant PrePeersSearchBar.
 */
const props: PrePeersSearchBarProps = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  submitLabel: {
    type: String,
    default: 'Rechercher',
  },
  inputId: {
    type: String,
    default: undefined,
  },
  showSubmitButton: {
    type: Boolean,
    default: true,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  search: [value: string]
}>()

const localQuery: Ref<string> = ref<string>(props.modelValue)

watch(
  (): string => props.modelValue,
  (next: string): void => {
    localQuery.value = next
  },
)

watch(localQuery, (next: string): void => {
  emit('update:modelValue', next)
})

const inputId: ComputedRef<string> = computed((): string => props.inputId ?? FALLBACK_INPUT_ID)

function emitSearch(): void {
  emit('search', localQuery.value.trim())
}
</script>

<style scoped>
.prepeers-search-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: var(--space-3);
  width: 100%;
  max-width: var(--container-lg);
  margin: 0 auto;
}

.prepeers-search-bar__field-wrap {
  position: relative;
  flex: 1 1 220px;
  min-width: 0;
}

.prepeers-search-bar__icon {
  position: absolute;
  left: var(--space-4);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.prepeers-search-bar__input {
  width: 100%;
  min-height: 44px;
  padding: 0.65em 1em 0.65em calc(var(--space-4) + 18px + var(--space-3));
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  outline: none;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
}

.prepeers-search-bar__input::placeholder {
  color: var(--text-muted);
}

.prepeers-search-bar__input:focus {
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 3px rgba(3, 160, 241, 0.2);
}

.prepeers-search-bar__btn {
  flex: 0 0 auto;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
}
</style>
